import Phaser from 'phaser';
import { TILESET_KEY, TX, cropTextureKey, itemIconKey } from './keys';
import { CROPS } from '../data/crops';

/**
 * Real asset files to load when present. EMPTY in Tier 1 — all art is generated
 * (see textures.ts). To swap in real art: drop the file in public/assets/ and add
 * one line here using the matching registry key from keys.ts. The placeholder for
 * that key is then automatically skipped (textures.ts guards on textures.exists).
 *
 * Example once you have the Sprout Lands tileset:
 *   IMAGE_ASSETS.push({ key: TILESET_KEY, url: 'assets/tiles/terrain.png' });
 */
export interface ImageAsset {
  key: string;
  url: string;
}
export interface SpriteSheetAsset {
  key: string;
  url: string;
  frameWidth: number;
  frameHeight: number;
}
export interface AudioAsset {
  key: string;
  urls: string[];
}

// Real art from the Sprout Lands pack (built by scripts/build-assets.py). These
// override the placeholders for their keys; everything else stays generated.
export const IMAGE_ASSETS: ImageAsset[] = [
  { key: TILESET_KEY, url: 'assets/tiles/terrain.png' },
  { key: TX.egg, url: 'assets/sprites/egg.png' },
  // The family photo is NOT bundled — it's device-local user data (see personalization.ts).
];
export const SPRITESHEET_ASSETS: SpriteSheetAsset[] = [
  { key: TX.player, url: 'assets/sprites/player.png', frameWidth: 48, frameHeight: 48 },
  { key: TX.playerAction, url: 'assets/sprites/player_actions.png', frameWidth: 48, frameHeight: 48 },
  { key: TX.chicken, url: 'assets/sprites/chicken.png', frameWidth: 16, frameHeight: 16 },
  { key: TX.cow, url: 'assets/sprites/cow.png', frameWidth: 32, frameHeight: 32 },
];
export const AUDIO_ASSETS: AudioAsset[] = [];

// Real crop art (Fantasy Crops by apsyll), sliced per crop per stage.
for (const c of Object.values(CROPS)) {
  for (let s = 0; s < c.stages; s++) {
    IMAGE_ASSETS.push({ key: cropTextureKey(c.id, s), url: `assets/crops/${c.id}_${s}.png` });
  }
}

// Real indoor furniture (Sprout Lands), keyed to each decoration's world texture + bag icon.
for (const id of ['painting', 'table_lamp', 'houseplant', 'chair', 'side_table', 'wall_clock', 'area_rug']) {
  IMAGE_ASSETS.push({ key: `tx_deco_${id}`, url: `assets/sprites/furn_${id}.png` });
  IMAGE_ASSETS.push({ key: itemIconKey(id), url: `assets/sprites/furn_${id}.png` });
}
// Multi-tile furniture: full sprite for the world, a scaled 16x16 for the bag icon.
for (const id of ['bed_blue', 'bed_pink', 'dresser']) {
  IMAGE_ASSETS.push({ key: `tx_deco_${id}`, url: `assets/sprites/furn_${id}.png` });
  IMAGE_ASSETS.push({ key: itemIconKey(id), url: `assets/sprites/furn_${id}_icon.png` });
}

/** Queue every real-file asset for loading. Call in PreloadScene.preload(). */
export function queueRealAssets(load: Phaser.Loader.LoaderPlugin): void {
  for (const a of IMAGE_ASSETS) load.image(a.key, a.url);
  for (const a of SPRITESHEET_ASSETS) {
    load.spritesheet(a.key, a.url, { frameWidth: a.frameWidth, frameHeight: a.frameHeight });
  }
  for (const a of AUDIO_ASSETS) load.audio(a.key, a.urls);
}
